import { type Snapshot, snapshotKeys } from '@taler/api-client'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'

export type { Snapshot }

export interface UseAccountReconciliationOptions {
  readonly ledgerId: () => string | undefined
  readonly accountId: () => string | undefined
  readonly enabled: () => boolean
}

export interface UseAccountReconciliation {
  readonly snapshots: ComputedRef<readonly Snapshot[]>
  readonly isLoadingHistory: ComputedRef<boolean>
  readonly historyError: ComputedRef<Error | null>
  retryHistory(): void
}

/** Read-only: recording a reconciliation now lives in `@/features/reconciliation`. */
export function useAccountReconciliation(options: UseAccountReconciliationOptions): UseAccountReconciliation {
  const api = useApi()

  const active = computed(
    () => options.enabled() && options.ledgerId() !== undefined && options.accountId() !== undefined,
  )

  const historyQuery = useQuery({
    queryKey: computed(() => snapshotKeys.list(options.ledgerId() ?? '', options.accountId() ?? '')),
    queryFn: ({ signal }) => api.snapshots.list(options.ledgerId()!, options.accountId()!, signal),
    enabled: active,
    retry: false,
  })

  return {
    snapshots: computed(() => historyQuery.data.value ?? []),
    isLoadingHistory: computed(() => active.value && historyQuery.isPending.value),
    historyError: computed(() => historyQuery.error.value),
    retryHistory: () => {
      void historyQuery.refetch()
    },
  }
}
